import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Typography, Box, Button, MenuItem,
   Avatar, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Modal, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid, Skeleton, Tooltip
} from '@mui/material';
import OverflowTooltip from '../../shared/OverflowTooltip';
import { Category, Close, Flag } from '@mui/icons-material';
import { getPriorityColor } from '../../shared/commonFunctions';
import { DATE_RANGES, DATE_RANGE_LABELS, CUSTOM_SELECTED, DATE_PICKER_MONTHS } from '../../shared/appConstants';
import Navbar from '../shared/Navbar';
import { fetchTicketSummary, fetchOverdueTasks, fetchTicketsCount, fetchTicketsList } from '../../store/reportSlice';
import { fetchLastSelectedProject, fetchProjectDropdownsByUser, fetchTicketDropdowns } from '../../store/ticketSlice';
import { PieChartView, BarChartView } from './ChartComponents';
import DataLimitSelector from './DataLimitSelector';
import PopperSelect from '../../shared/components/PopperSelect';
import PopperDateField from '../../shared/components/PopperDateField';
import FilterHeader from '../dashboard/FilterHeader';
import { reportStyles } from '../../shared/globalSX';
import { ICONS } from '../../shared/icons';
import { createCategoryTableConfig, createDailyTicketCreationTableConfig } from './reportExportUtils';
import './Report.scss';
import { heatmapColors, COLORS, REPORT_CHART_COLORS } from '../../shared/colors';
import TicketRow from '../../../src/components/TicketRow';
import useCloseOnScroll from '../../shared/useCloseOnScroll';
import TaskTypeChart from './components/TaskTypeChart';
import LabelsChart from './components/LabelsChart';
import PriorityChart from './components/PriorityChart';
import AssigneeChart from './components/AssigneeChart';
import DueDateTable from './components/DueDateTable';
import CalendarChart from './components/CalendarChart';
import StatusChart from './components/StatusChart';




const ReportsDateField = ({ value, onChange, ariaLabel }) => (
  <PopperDateField
    value={value || ''}
    onChange={onChange}
    ariaLabel={ariaLabel}
    icon={ICONS.dropdownDueDate}
    className="reports-date-field"
    iconClassName="reports-date-picker-icon"
    buttonClassName="reports-date-picker-button"
    iconUrlVar="--reports-date-icon-url"
  />
);

const Dashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const tr = t;
  const { ticketSummary, overdueTasks, ticketsCount, ticketsList, loading, overdueLoading, ticketsCountLoading } = useSelector((state) => state.report);
  const { projectDropdownsByUser, dropdowns, filters: ticketFilters, lastSelectedProjectId } = useSelector((state) => state.tickets);
  const [activeActionTooltip, setActiveActionTooltip] = useState(null);
  const reportIconTooltipSlotProps = useMemo(() => ({
    tooltip: { className: 'app-tooltip' },
    popper: {
      disablePortal: true,
      modifiers: [
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            altBoundary: true,
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom', 'left', 'right'],
            padding: 8,
          },
        },
        {
          name: 'offset',
          options: {
            offset: [0, 8],
          },
        },
      ],
    },
  }), []);

  const showTicketsDueDateColumn = useMemo(() => (
    Array.isArray(ticketsList) && ticketsList.some((ticket) => Boolean(ticket?.due_date))
  ), [ticketsList]);


  const savedFilters = useMemo(() => {
    try {
      const saved = localStorage.getItem('reportsFilterValues');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  }, []);

  const normalizeProjectId = useCallback((projectId) => {
    if (!projectId || projectId === 'all') return null;
    return projectId;
  }, []);

  const [selectedProject, setSelectedProject] = useState(
    normalizeProjectId(ticketFilters?.selectedProject) ||
    normalizeProjectId(savedFilters.selectedProject) ||
    ''
  );
  const [selectedAssignee, setSelectedAssignee] = useState(savedFilters.selectedAssignee || ['all']);
  const [selectedTaskType, setSelectedTaskType] = useState(savedFilters.selectedTaskType || ['all']);
  const [selectedLabels, setSelectedLabels] = useState(savedFilters.selectedLabels || ['all']);
  const [selectedPriorities, setSelectedPriorities] = useState(savedFilters.selectedPriorities || ['all']);
  const [dueFrom, setDueFrom] = useState(savedFilters.dueFrom || '');
  const [dueTo, setDueTo] = useState(savedFilters.dueTo || '');
  const [chartView, setChartView] = useState('pie'); // 'pie', 'bar', 'text'
  const [statusChartView, setStatusChartView] = useState('bar');
  const [taskTypeChartView, setTaskTypeChartView] = useState('pie');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [priorityChartView, setPriorityChartView] = useState('pie');
  const [labelsChartView, setLabelsChartView] = useState('pie');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [assigneeChartModalOpen, setAssigneeChartModalOpen] = useState(false);
  const [statusChartModalOpen, setStatusChartModalOpen] = useState(false);
  const [taskTypeChartModalOpen, setTaskTypeChartModalOpen] = useState(false);
  const [labelsChartModalOpen, setLabelsChartModalOpen] = useState(false);
  const [priorityChartModalOpen, setPriorityChartModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState(savedFilters.dateRange || DATE_RANGES.SELECT_TIME);
  const [customDateFrom, setCustomDateFrom] = useState(savedFilters.customDateFrom || '');
  const [customDateTo, setCustomDateTo] = useState(savedFilters.customDateTo || '');
  const [tempDateFrom, setTempDateFrom] = useState('');
  const [tempDateTo, setTempDateTo] = useState('');
  const [isCustomRangeModalOpen, setIsCustomRangeModalOpen] = useState(false);
  const [dueDateFilter, setDueDateFilter] = useState('overdue');
  const [areChartsReady, setAreChartsReady] = useState(false);
  const [taskTypeLimit, setTaskTypeLimit] = useState('5');
  const [labelsLimit, setLabelsLimit] = useState('5');
  const [priorityLimit, setPriorityLimit] = useState('5');
  const [assigneeLimit, setAssigneeLimit] = useState('5');
  const [visibleFilters, setVisibleFilters] = useState(() => {
    try {
      const saved = localStorage.getItem('reportsVisibleFilters');
      const parsed = saved ? JSON.parse(saved) : [];
      return ['project', ...parsed.filter(id => id !== 'project')];
    } catch (e) {
      return ['project'];
    }
  });
  const lastProjectContextRef = useRef(null);

  useCloseOnScroll(null, useCallback(() => {
    setActiveActionTooltip(null);
  }, []));

  useEffect(() => {
    try {
      localStorage.setItem('reportsVisibleFilters', JSON.stringify(visibleFilters));
    } catch (e) {
      console.warn('Storage quota exceeded:', e);
    }
  }, [visibleFilters]);

  useEffect(() => {
    try {
      const filterValues = {
        selectedProject,
        selectedAssignee,
        selectedTaskType,
        selectedLabels,
        selectedPriorities,
        dueFrom,
        dueTo,
        dateRange,
        customDateFrom,
        customDateTo
      };
      localStorage.setItem('reportsFilterValues', JSON.stringify(filterValues));
    } catch (e) {
      console.warn('Storage quota exceeded:', e);
    }
  }, [
    selectedProject,
    selectedAssignee,
    selectedTaskType,
    selectedLabels,
    selectedPriorities,
    dueFrom,
    dueTo,
    dateRange,
    customDateFrom,
    customDateTo
  ]);

  const resetReportFiltersForProject = useCallback((projectId) => {
    setSelectedProject(projectId || '');
    setSelectedAssignee(['all']);
    setSelectedTaskType(['all']);
    setSelectedLabels(['all']);
    setSelectedPriorities(['all']);
    setDueFrom('');
    setDueTo('');
    setDateRange(DATE_RANGES.SELECT_TIME);
    setCustomDateFrom('');
    setCustomDateTo('');
    setDueDateFilter('overdue');
    setSelectedStatuses([]);
    setTaskTypeLimit('5');
    setLabelsLimit('5');
    setPriorityLimit('5');
    setAssigneeLimit('5');
  }, []);

  const handleRemoveFilter = useCallback((filterId) => {
    if (filterId !== 'project') {
      setVisibleFilters(prev => prev.filter(id => id !== filterId));
    }
    if (filterId === 'assignee') setSelectedAssignee(['all']);
    if (filterId === 'taskType') setSelectedTaskType(['all']);
    if (filterId === 'labels') setSelectedLabels(['all']);
    if (filterId === 'priorities') setSelectedPriorities(['all']);
    if (filterId === 'dateRange') {
      setDateRange(DATE_RANGES.SELECT_TIME);
      setCustomDateFrom('');
      setCustomDateTo('');
    }
    if (filterId === 'dueFrom') setDueFrom('');
    if (filterId === 'dueTo') setDueTo('');
  }, []);

  const handleAddFilterSelect = useCallback((filterId) => {
    setVisibleFilters(prev => (!prev.includes(filterId) ? [...prev, filterId] : prev));
  }, []);

  const availableFilters = useMemo(() => [
    { id: 'project', label: tr('dashboard.project'), icon: ICONS.dropdownProject },
    { id: 'dateRange', label: tr('reports.dateRange'), icon: ICONS.dropdownDateRange },
    { id: 'assignee', label: tr('reports.assignee'), icon: ICONS.dropdownAssignee },
    { id: 'taskType', label: tr('reports.taskType'), icon: ICONS.dropdownTaskType },
    { id: 'labels', label: tr('reports.labels'), icon: ICONS.dropdownLabels },
    { id: 'priorities', label: tr('reports.priorities'), icon: ICONS.dropdownPriority },
    { id: 'dueFrom', label: tr('reports.dueFrom'), icon: ICONS.dropdownDueDate },
    { id: 'dueTo', label: tr('reports.dueTo'), icon: ICONS.dropdownDueDate },
  ], [tr]);

  const reportFilterDropdowns = useMemo(() => ({
    project: () => (
      <PopperSelect
        value={selectedProject}
        onChange={(e) => resetReportFiltersForProject(e.target.value)}
        displayEmpty
        renderValue={(value) => {
          if (!value || value === 'all') return tr('dashboard.selectProject');
          return projectDropdownsByUser?.find?.((project) => String(project.id) === String(value))?.name || tr('dashboard.unavailableFilterValue');
        }}
        MenuProps={{ PaperProps: { className: 'reports-dropdown-menu' } }}
      >
        {projectDropdownsByUser?.length === 0 && (
          <MenuItem value="all">{tr('dashboard.selectProject')}</MenuItem>
        )}
        {projectDropdownsByUser?.map?.((project) => (
          <MenuItem key={project.id} value={project.id}>
            <Box className="reports-menu-item-flex">
              <Avatar className="reports-menu-avatar reports-menu-avatar-project">
                {project.name?.charAt(0) || tr('dashboard.projectInitial')}
              </Avatar>
              <OverflowTooltip title={project.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
            </Box>
          </MenuItem>
        ))}
      </PopperSelect>
    ),
    assignee: () => (
      <PopperSelect
        multiple
        value={selectedAssignee}
        onChange={(e) => {
          let newValue = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          if (newValue.includes('all')) {
            newValue = selectedAssignee.includes('all') ? newValue.filter(v => v !== 'all') : ['all'];
          } else if (newValue.length === 0) {
            newValue = ['all'];
          }
          setSelectedAssignee(newValue);
        }}
        renderValue={(selected) => {
          if (selected.includes('all')) return tr('reports.allPeople');
          return (
            <Box className="reports-filter-chip-container">
              {selected.slice(0, 2).map((id) => {
                const name = (dropdowns.assignees || []).find(a => a.id === id)?.name || id;
                return <OverflowTooltip key={id} title={name} tooltipWidth={350} component={Box} sx={{ display: 'inline-block' }}><Chip label={name} size="small" className="reports-filter-chip" sx={reportStyles.filterChip(COLORS.success)} /></OverflowTooltip>;
              })}
              {selected.length > 2 && <Chip label={`+${selected.length - 2}`} size="small" sx={reportStyles.filterChipExtra} />}
            </Box>
          );
        }}
        displayEmpty
        sx={reportStyles.selectHeight40}
        MenuProps={{ PaperProps: { className: 'reports-dropdown-menu' } }}
      >
        <MenuItem value="all">{tr('reports.allPeople')}</MenuItem>
        {(dropdowns.assignees || []).map((a) => (
          <MenuItem key={a.id} value={a.id}>
            <Box className="reports-menu-item-flex">
              <Avatar className="reports-menu-avatar reports-menu-avatar-assignee">{a.name?.charAt(0) || 'A'}</Avatar>
              <OverflowTooltip title={a.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
            </Box>
          </MenuItem>
        ))}
      </PopperSelect>
    ),
    taskType: () => (
      <PopperSelect
        multiple
        value={selectedTaskType}
        onChange={(e) => {
          let newValue = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          if (newValue.includes('all')) {
            newValue = selectedTaskType.includes('all') ? newValue.filter(v => v !== 'all') : ['all'];
          } else if (newValue.length === 0) {
            newValue = ['all'];
          }
          setSelectedTaskType(newValue);
        }}
        renderValue={(selected) => {
          if (selected.includes('all')) return tr('reports.allTypes');
          return (
            <Box className="reports-filter-chip-container">
              {selected.slice(0, 2).map((id) => {
                const name = (dropdowns.types || []).find(t => t.id === id)?.name || id;
                return <OverflowTooltip key={id} title={name} tooltipWidth={350} component={Box} sx={{ display: 'inline-block' }}><Chip label={name} size="small" className="reports-filter-chip" sx={reportStyles.filterChip(COLORS.purple[500])} /></OverflowTooltip>;
              })}
              {selected.length > 2 && <Chip label={`+${selected.length - 2}`} size="small" sx={reportStyles.filterChipExtra} />}
            </Box>
          );
        }}
        displayEmpty
        sx={reportStyles.selectHeight40}
        MenuProps={{ PaperProps: { className: 'reports-dropdown-menu' } }}
      >
        <MenuItem value="all">{tr('reports.allTypes')}</MenuItem>
        {(dropdowns.types || []).map((type) => (
          <MenuItem key={type.id} value={type.id}>
            <Box className="reports-menu-item-flex">
              <Avatar className="reports-menu-avatar reports-menu-avatar-task-type"><Category /></Avatar>
              <OverflowTooltip title={type.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
            </Box>
          </MenuItem>
        ))}
      </PopperSelect>
    ),
    labels: () => (
      <PopperSelect
        multiple
        value={selectedLabels}
        onChange={(e) => {
          let newValue = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          if (newValue.includes('all')) {
            newValue = selectedLabels.includes('all') ? newValue.filter(v => v !== 'all') : ['all'];
          } else if (newValue.length === 0) {
            newValue = ['all'];
          }
          setSelectedLabels(newValue);
        }}
        renderValue={(selected) => {
          if (selected.includes('all')) return tr('reports.allLabels');
          return (
            <Box className="reports-filter-chip-container">
              {selected.slice(0, 2).map((id) => {
                const lbl = (dropdowns.labels || []).find(l => l.id === id);
                const name = lbl?.name || id;
                return <OverflowTooltip key={id} title={name} tooltipWidth={350} component={Box} sx={{ display: 'inline-block' }}><Chip label={name} size="small" className="reports-filter-chip" sx={reportStyles.filterChip(lbl?.colour || COLORS.brand.primaryDark)} /></OverflowTooltip>;
              })}
              {selected.length > 2 && <Chip label={`+${selected.length - 2}`} size="small" sx={reportStyles.filterChipExtra} />}
            </Box>
          );
        }}
        displayEmpty
        sx={reportStyles.selectHeight40}
        MenuProps={{ PaperProps: { className: 'reports-dropdown-menu' } }}
      >
        <MenuItem value="all">{tr('reports.allLabels')}</MenuItem>
        {(dropdowns.labels || []).map((lbl) => (
          <MenuItem key={lbl.id} value={lbl.id}>
            <Box className="reports-menu-item-flex">
              <Box className="reports-label-circle" style={{ backgroundColor: lbl.colour || COLORS.primary }} />
              <OverflowTooltip title={lbl.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
            </Box>
          </MenuItem>
        ))}
      </PopperSelect>
    ),
    priorities: () => (
      <PopperSelect
        multiple
        value={selectedPriorities}
        onChange={(e) => {
          let newValue = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
          if (newValue.includes('all')) {
            newValue = selectedPriorities.includes('all') ? newValue.filter(v => v !== 'all') : ['all'];
          } else if (newValue.length === 0) {
            newValue = ['all'];
          }
          setSelectedPriorities(newValue);
        }}
        renderValue={(selected) => {
          if (selected.includes('all')) return tr('reports.allPriorities');
          return (
            <Box className="reports-filter-chip-container">
              {selected.slice(0, 2).map((id) => {
                const name = (dropdowns.priorities || []).find(p => p.id === id)?.name || id;
                return <OverflowTooltip key={id} title={name} tooltipWidth={350} component={Box} sx={{ display: 'inline-block' }}><Chip label={name} size="small" className="reports-filter-chip" sx={reportStyles.filterChip(COLORS.brand.primaryDark)} /></OverflowTooltip>;
              })}
              {selected.length > 2 && <Chip label={`+${selected.length - 2}`} size="small" sx={reportStyles.filterChipExtra} />}
            </Box>
          );
        }}
        displayEmpty
        sx={reportStyles.selectHeight40}
        MenuProps={{ PaperProps: { className: 'reports-dropdown-menu' } }}
      >
        <MenuItem value="all">{tr('reports.allPriorities')}</MenuItem>
        {(dropdowns.priorities || []).map((p) => (
          <MenuItem key={p.id} value={p.id}>
            <Box className="reports-menu-item-flex">
              <Avatar className="reports-menu-avatar reports-menu-avatar-priority" style={{ backgroundColor: getPriorityColor(p, { priorities: dropdowns.priorities }) }}><Flag /></Avatar>
              <OverflowTooltip title={p.name} tooltipWidth={350} className="overflow-tooltip-dropdown" placement="top-start" />
            </Box>
          </MenuItem>
        ))}
      </PopperSelect>
    ),
    dateRange: () => (
      <PopperSelect
        value={dateRange === DATE_RANGES.CUSTOM && customDateFrom && customDateTo ? CUSTOM_SELECTED : dateRange}
        onChange={(e) => handleDateRangeChange(e.target.value === CUSTOM_SELECTED ? DATE_RANGES.CUSTOM : e.target.value)}
        displayEmpty
        sx={reportStyles.selectHeight40}
        renderValue={(value) => value === CUSTOM_SELECTED ? `${customDateFrom} ${tr('reports.to')} ${customDateTo}` : (DATE_RANGE_LABELS[value] || value)}
        MenuProps={{ PaperProps: { style: { maxHeight: 300, overflow: 'auto' } } }}
      >
        {Object.entries(DATE_RANGE_LABELS).map(([key, label]) => (
          <MenuItem key={key} value={key}>{label}</MenuItem>
        ))}
        <MenuItem value={DATE_RANGES.CUSTOM}>{DATE_RANGE_LABELS[DATE_RANGES.CUSTOM]}</MenuItem>
      </PopperSelect>
    ),
    dueFrom: () => (
      <ReportsDateField
        value={dueFrom}
        onChange={(e) => setDueFrom(e.target.value)}
        ariaLabel={tr('reports.dueFrom')}
      />
    ),
    dueTo: () => (
      <ReportsDateField
        value={dueTo}
        onChange={(e) => setDueTo(e.target.value)}
        ariaLabel={tr('reports.dueTo')}
      />
    ),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [selectedProject, selectedAssignee, selectedTaskType, selectedLabels, selectedPriorities, dateRange, customDateFrom, customDateTo, dueFrom, dueTo, dropdowns, projectDropdownsByUser, resetReportFiltersForProject, tr]);

  const [pinnedCharts, setPinnedCharts] = useState(() => {
    try {
      const saved = localStorage.getItem('reportsPinnedCharts');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('reportsPinnedCharts', JSON.stringify(pinnedCharts));
    } catch (e) {
      console.warn('Storage quota exceeded:', e);
    }
  }, [pinnedCharts]);

  const handleTogglePin = useCallback((chartId) => {
    setPinnedCharts(prev => (prev.includes(chartId) 
      ? prev.filter(id => id !== chartId) 
      : [...prev, chartId]
    ));
  }, []);

  useEffect(() => {
    dispatch(fetchProjectDropdownsByUser());
    dispatch(fetchLastSelectedProject());
  }, [dispatch]);



  useEffect(() => {
    if (!projectDropdownsByUser || projectDropdownsByUser.length === 0) {
      return;
    }

    const projectExists = (projectId) => (
      projectDropdownsByUser.some(project => String(project.id) === String(projectId))
    );

    const preferredProjectId = normalizeProjectId(ticketFilters?.selectedProject)
      || normalizeProjectId(lastSelectedProjectId)
      || normalizeProjectId(savedFilters.selectedProject);

    const nextProjectId = (preferredProjectId && projectExists(preferredProjectId))
      ? preferredProjectId
      : projectDropdownsByUser[0]?.id || '';

    if (!nextProjectId) {
      return;
    }

    const nextProjectIdAsString = String(nextProjectId);
    const savedProjectIdAsString = String(normalizeProjectId(savedFilters.selectedProject) || '');

    if (lastProjectContextRef.current !== nextProjectIdAsString) {
      lastProjectContextRef.current = nextProjectIdAsString;

      if (savedProjectIdAsString !== nextProjectIdAsString) {
        resetReportFiltersForProject(nextProjectId);
      } else {
        setSelectedProject((currentSelectedProject) => {
          if (!currentSelectedProject || String(currentSelectedProject) !== nextProjectIdAsString || !projectExists(currentSelectedProject)) {
            return nextProjectId;
          }

          return currentSelectedProject;
        });
      }
    }
  }, [
    projectDropdownsByUser,
    ticketFilters?.selectedProject,
    lastSelectedProjectId,
    savedFilters.selectedProject,
    normalizeProjectId,
    resetReportFiltersForProject
  ]);

  useEffect(() => {
    if (!selectedProject) return;
    dispatch(fetchTicketDropdowns(selectedProject));
  }, [dispatch, selectedProject]);

  const calculateDateRange = (dateRange, customDateFrom, customDateTo) => {
    const today = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case DATE_RANGES.TODAY:
        startDate = endDate = today.toISOString().split('T')[0];
        break;
      case DATE_RANGES.YESTERDAY:
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        startDate = endDate = yesterday.toISOString().split('T')[0];
        break;
      case DATE_RANGES.THIS_WEEK:
        const dayOfWeek = today.getDay();
        const startOfWeek = new Date(today.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
        const endOfWeek = new Date(startOfWeek.getTime() + 6 * 24 * 60 * 60 * 1000);
        startDate = startOfWeek.toISOString().split('T')[0];
        endDate = endOfWeek.toISOString().split('T')[0];
        break;
      case DATE_RANGES.THIS_MONTH:
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      case DATE_RANGES.LAST_MONTH:
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().split('T')[0];
        endDate = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case DATE_RANGES.LAST_7_DAYS:
        startDate = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case DATE_RANGES.LAST_14_DAYS:
        startDate = new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case DATE_RANGES.LAST_30_DAYS:
        startDate = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case DATE_RANGES.CUSTOM:
        if (customDateFrom && customDateTo) {
          startDate = customDateFrom;
          endDate = customDateTo;
        }
        break;
      default:
        break;
    }

    return { startDate, endDate };
  };

  // Fetch ticket summary on component mount and filter changes
  useEffect(() => {
    if (!selectedProject) return;

    const actionTypes = ['status', 'task_type', 'assignee', 'labels', 'priority'];

    const params = {
      action_type: actionTypes.join(','),
    };

    params.projects = selectedProject;
    if (!selectedAssignee.includes('all')) params.assignees = selectedAssignee.join(',');
    if (!selectedTaskType.includes('all')) params.task_type = selectedTaskType.join(',');
    if (!selectedLabels.includes('all')) params.labels = selectedLabels.join(',');
    if (!selectedPriorities.includes('all')) params.priorities = selectedPriorities.join(',');
    if (dueFrom) params.due_from = dueFrom;
    if (dueTo) params.due_to = dueTo;

    if (dateRange && dateRange !== DATE_RANGES.SELECT_TIME) {
      const { startDate, endDate } = calculateDateRange(dateRange, customDateFrom, customDateTo);
      if (startDate && endDate) {
        params.from_date = startDate;
        params.to_date = endDate;
      }
    }

    setSelectedStatuses([]);
    setTaskTypeLimit('5');
    setLabelsLimit('5');
    setPriorityLimit('5');
    setAssigneeLimit('5');
    dispatch(fetchTicketSummary(params));
  }, [dispatch, selectedProject, selectedAssignee, selectedTaskType, selectedLabels, selectedPriorities, dueFrom, dueTo, dateRange, customDateFrom, customDateTo]);

  // Transform API data to chart format with stable references
  const getChartData = useCallback((dataKey, colorMap) => {
    if (!ticketSummary) return null;
    if (!ticketSummary[dataKey]) return [];
    
    return ticketSummary[dataKey].map((item, index) => ({
      name: item.key,
      tickets: item.count,
      color: colorMap[item.key] || REPORT_CHART_COLORS[index % REPORT_CHART_COLORS.length]
    }));
  }, [ticketSummary]);

  const statusTicketDataAll = useMemo(() => getChartData('status', {}) || [], [getChartData]);
  const availableStatuses = useMemo(() => statusTicketDataAll?.map(s => s.name) || [], [statusTicketDataAll]);

  useEffect(() => {
    const statuses = statusTicketDataAll?.map(s => s.name) || [];
    if (statuses.length > 0) {
      setSelectedStatuses(prevSelected => {
        if (prevSelected.length === 0) {
          return statuses.slice(0, Math.min(5, statuses.length));
        } else {
          const validStatuses = prevSelected.filter(s => statuses.includes(s));
          if (validStatuses.length === 0) {
            return statuses.slice(0, Math.min(5, statuses.length));
          } else if (validStatuses.length !== prevSelected.length) {
            return validStatuses;
          }
          return prevSelected;
        }
      });
    }
  }, [statusTicketDataAll]);

  const statusTicketData = useMemo(() => statusTicketDataAll?.filter(status => selectedStatuses.includes(status.name)), [statusTicketDataAll, selectedStatuses]);

  const applyLimit = useCallback((data, limit) => {
    if (!data) return null;
    if (limit === 'all') return data;
    return data.slice(0, parseInt(limit));
  }, []);

  const taskTypeTicketData = useMemo(() => applyLimit(getChartData('task_type', {}), taskTypeLimit), [getChartData, applyLimit, taskTypeLimit]);
  const labelsTicketData = useMemo(() => applyLimit(getChartData('labels', {}), labelsLimit), [getChartData, applyLimit, labelsLimit]);
  const priorityTicketDataAPI = useMemo(() => applyLimit(getChartData('priority', {}), priorityLimit), [getChartData, applyLimit, priorityLimit]);
  const assigneeTicketData = useMemo(() => applyLimit(getChartData('assignee', {}), assigneeLimit), [getChartData, applyLimit, assigneeLimit]);

  const handleSetChartView = useCallback((view) => setChartView(view), []);
  const handleSetStatusChartView = useCallback((view) => setStatusChartView(view), []);
  const handleSetTaskTypeChartView = useCallback((view) => setTaskTypeChartView(view), []);
  const handleSetLabelsChartView = useCallback((view) => setLabelsChartView(view), []);
  const handleSetPriorityChartView = useCallback((view) => setPriorityChartView(view), []);



  useEffect(() => {
    if (!selectedProject) return;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Calculate Monday of current week
    const monday = new Date(today);
    const dayOfWeek = today.getDay();
    const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    monday.setDate(today.getDate() + daysToMonday);

    // Calculate Sunday of current week
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const params = {};

    params.projects = selectedProject;
    if (!selectedAssignee.includes('all')) params.assignees = selectedAssignee.join(',');
    if (!selectedTaskType.includes('all')) params.task_type = selectedTaskType.join(',');
    if (!selectedLabels.includes('all')) params.labels = selectedLabels.join(',');
    if (!selectedPriorities.includes('all')) params.priorities = selectedPriorities.join(',');

    if (dateRange && dateRange !== DATE_RANGES.SELECT_TIME) {
      const { startDate, endDate } = calculateDateRange(dateRange, customDateFrom, customDateTo);
      if (startDate && endDate) {
        params.created_from = startDate;
        params.created_to = endDate;
      }
    }

    if (dueDateFilter === 'overdue') {
      // Don't send due_from for overdue filter
    } else if (dueDateFilter === 'today') {
      params.due_from = formatDate(today);
      params.due_to = formatDate(today);
    } else if (dueDateFilter === 'week') {
      params.due_from = formatDate(monday);
      params.due_to = formatDate(sunday);
    }

    if (dueDateFilter !== 'all') {
      dispatch(fetchOverdueTasks(params));
    }
  }, [dispatch, dueDateFilter, selectedProject, selectedAssignee, selectedTaskType, selectedLabels, selectedPriorities, dateRange, customDateFrom, customDateTo]);

  const dueDateData = overdueTasks;

  // Enable export button after 3 seconds to allow charts to render and animations to complete
  useEffect(() => {
    setAreChartsReady(false);

    const timeout = setTimeout(() => {
      setAreChartsReady(true);
    }, 3000);

    return () => {
      clearTimeout(timeout);
    };
  }, [ticketSummary, ticketsCount, selectedProject, selectedAssignee, selectedTaskType, selectedLabels, selectedPriorities, chartView, statusChartView, taskTypeChartView, priorityChartView, labelsChartView]);

  // Fetch tickets count for calendar
  useEffect(() => {
    if (!selectedProject) return;

    const params = {
      month: selectedMonth + 1,
      year: selectedYear
    };

    params.projects = selectedProject;
    if (!selectedAssignee.includes('all')) params.assignees = selectedAssignee.join(',');
    if (!selectedTaskType.includes('all')) params.task_type = selectedTaskType.join(',');
    if (!selectedLabels.includes('all')) params.labels = selectedLabels.join(',');
    if (!selectedPriorities.includes('all')) params.priorities = selectedPriorities.join(',');

    dispatch(fetchTicketsCount(params));
  }, [dispatch, selectedMonth, selectedYear, selectedProject, selectedAssignee, selectedTaskType, selectedLabels, selectedPriorities]);

  const generateCalendarData = (month, year) => {
    if (ticketsCount === null) return null;
    
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const calendar = [];

    // Convert ticketsCount array to object for easier lookup
    const ticketCountMap = Array.isArray(ticketsCount)
      ? ticketsCount.reduce((acc, item) => {
        acc[item.date] = item.count;
        return acc;
      }, {})
      : {};

    // Add empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      calendar.push({ day: null, tickets: 0 });
    }

    // Add days of the month with ticket counts from API
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      calendar.push({
        day,
        tickets: ticketCountMap[dateKey] || 0,
        date: dateKey
      });
    }

    // Wrap around logic: if calendar exceeds 35 slots (5 rows), 
    // move extra days to the empty slots at the beginning of the first row
    if (calendar.length > 35) {
      const extraDays = calendar.splice(35);
      extraDays.forEach((extra, idx) => {
        if (calendar[idx] && calendar[idx].day === null) {
          calendar[idx] = extra;
        }
      });
    }

    return calendar;
  };

  const calendarData = generateCalendarData(selectedMonth, selectedYear);

  const getDateRangeMonthSpan = () => {
    if (dateRange === DATE_RANGES.CUSTOM && customDateFrom && customDateTo) {
      const startDate = new Date(customDateFrom);
      const endDate = new Date(customDateTo);
      const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
      return { monthsDiff, startDate, endDate };
    }
    return { monthsDiff: 0, startDate: null, endDate: null };
  };

  const { monthsDiff, startDate } = getDateRangeMonthSpan();
  const isMultiMonthRange = monthsDiff >= 2;
  const isTwoMonthRange = monthsDiff === 1;

  const calendarData2 = isTwoMonthRange && startDate ? generateCalendarData(startDate.getMonth() + 1, startDate.getMonth() === 11 ? startDate.getFullYear() + 1 : startDate.getFullYear()) : [];

  const getTicketColor = (ticketCount) => {
    const colors = heatmapColors;

    if (ticketCount === 0) return colors[0];
    if (ticketCount === 1) return colors[1];
    if (ticketCount === 2) return colors[2];
    if (ticketCount === 3) return colors[3];
    if (ticketCount === 4) return colors[4];
    if (ticketCount === 5) return colors[5];
    return colors.max;
  };

  const handleClearFilters = () => {
    setSelectedAssignee(['all']);
    setSelectedTaskType(['all']);
    setSelectedLabels(['all']);
    setSelectedPriorities(['all']);
    setDueFrom('');
    setDueTo('');
    setDateRange(DATE_RANGES.SELECT_TIME);
    setCustomDateFrom('');
    setCustomDateTo('');
    setVisibleFilters(['project']);
  };

  const handleDateRangeChange = (value) => {
    if (value === DATE_RANGES.CUSTOM) {
      setTempDateFrom(customDateFrom);
      setTempDateTo(customDateTo);
      setIsCustomRangeModalOpen(true);
    } else {
      setDateRange(value);
      setCustomDateFrom('');
      setCustomDateTo('');
    }
  };

  const handleCustomRangeSubmit = () => {
    if (tempDateFrom && tempDateTo) {
      setCustomDateFrom(tempDateFrom);
      setCustomDateTo(tempDateTo);
      setDateRange(DATE_RANGES.CUSTOM);
    }
    setIsCustomRangeModalOpen(false);
  };

  const handleDateClick = (dayData) => {
    if (dayData.day && dayData.tickets > 0 && selectedProject) {
      setSelectedDate(dayData.date);

      const params = {
        date: dayData.date
      };

      params.projects = selectedProject;
      if (!selectedAssignee.includes('all')) params.assignees = selectedAssignee.join(',');
      if (!selectedTaskType.includes('all')) params.task_type = selectedTaskType.join(',');
      if (!selectedLabels.includes('all')) params.labels = selectedLabels.join(',');
      if (!selectedPriorities.includes('all')) params.priorities = selectedPriorities.join(',');

      dispatch(fetchTicketsList(params)).then(() => {
        setModalOpen(true);
      });
    }
  };

  const handleExportPDF = async () => {
    // Build base filters from current filter state
    const { startDate, endDate } = calculateDateRange(dateRange, customDateFrom, customDateTo);

    const baseFilters = {};
    if (selectedProject) baseFilters.projects = selectedProject;
    if (!selectedAssignee.includes('all')) baseFilters.assignees = selectedAssignee.join(',');
    if (!selectedTaskType.includes('all')) baseFilters.task_type = selectedTaskType.join(',');
    if (!selectedLabels.includes('all')) baseFilters.labels = selectedLabels.join(',');
    if (!selectedPriorities.includes('all')) baseFilters.priorities = selectedPriorities.join(',');
    if (dueFrom) baseFilters.due_from = dueFrom;
    if (dueTo) baseFilters.due_to = dueTo;
    if (startDate && endDate) {
      baseFilters.from_date = startDate;
      baseFilters.to_date = endDate;
    }

    const tables = [];

    // Upcoming Due Dates table
    const dueDatesTableConfig = {
      title: tr('reports.upcomingDueDates.title'),
      filterType: dueDateFilter,
      headers: [
        { label: tr('reports.upcomingDueDates.tableHeaders.title'), key: 'title' },
        { label: tr('reports.upcomingDueDates.tableHeaders.assignee'), key: 'assignee' },
        { label: tr('reports.upcomingDueDates.tableHeaders.startDate'), key: 'startDate' },
        { label: tr('reports.upcomingDueDates.tableHeaders.dueDate'), key: 'dueDate' },
      ],
      data: (dueDateData || []).map((row) => ({
        title: `${row.title}\n${row.ticket_prefix}-${row.ticket_number}`,
        assignee: row.assignees?.length > 0
          ? row.assignees.map(a => a.name).join(', ')
          : (row.assignee_name || tr('reports.upcomingDueDates.unassigned')),
        startDate: row.start_date ? new Date(row.start_date).toLocaleDateString() : '-',
        dueDate: new Date(row.due_date).toLocaleDateString(),
      })),
    };
    if (dueDatesTableConfig.data.length > 0) {
      tables.push(dueDatesTableConfig);
    }

    // Use existing chart data - no API calls needed

    // Task Type table
    if (taskTypeTicketData && taskTypeTicketData.length > 0) {
      const taskTypeTable = createCategoryTableConfig({
        title: 'Task Type vs Ticket Count',
        chartData: taskTypeTicketData,
        fieldKey: 'task_type',
        translate: tr
      });
      if (taskTypeTable) tables.push(taskTypeTable);
    }

    // Labels table
    if (labelsTicketData && labelsTicketData.length > 0) {
      const labelsTable = createCategoryTableConfig({
        title: 'Labels vs Ticket Count',
        chartData: labelsTicketData,
        fieldKey: 'labels',
        translate: tr
      });
      if (labelsTable) tables.push(labelsTable);
    }

    // Assignee table
    if (assigneeTicketData && assigneeTicketData.length > 0) {
      const assigneeTable = createCategoryTableConfig({
        title: 'Assignee vs Ticket Count',
        chartData: assigneeTicketData,
        fieldKey: 'assignee',
        translate: tr
      });
      if (assigneeTable) tables.push(assigneeTable);
    }

    // Priority table
    if (priorityTicketDataAPI && priorityTicketDataAPI.length > 0) {
      const priorityTable = createCategoryTableConfig({
        title: 'Priority vs Ticket Count',
        chartData: priorityTicketDataAPI,
        fieldKey: 'priority',
        translate: tr
      });
      if (priorityTable) tables.push(priorityTable);
    }

    // Status table
    if (statusTicketDataAll && statusTicketDataAll.length > 0) {
      const statusTable = createCategoryTableConfig({
        title: 'Status vs Ticket Count',
        chartData: statusTicketDataAll,
        fieldKey: 'status',
        translate: tr
      });
      if (statusTable) tables.push(statusTable);
    }

    // Daily Ticket Creation table
    if (ticketsCount && ticketsCount.length > 0) {
      const dailyTicketTable = createDailyTicketCreationTableConfig({
        title: 'Daily Ticket Creation',
        ticketsCount,
        translate: tr
      });
      if (dailyTicketTable) tables.push(dailyTicketTable);
    }

    // Export with all tables
    const { exportReportsDashboardToPDF } = await import('./reportExportUtils');
    await exportReportsDashboardToPDF({
      mainSelector: '.main',
      tables,
      filename: `reports-dashboard-${new Date().toISOString().split('T')[0]}.pdf`,
    });
  };

  const isAnyFilterActive = useMemo(() => {
    return (
      (selectedAssignee && !selectedAssignee.includes('all')) ||
      (selectedTaskType && !selectedTaskType.includes('all')) ||
      (selectedLabels && !selectedLabels.includes('all')) ||
      (selectedPriorities && !selectedPriorities.includes('all')) ||
      Boolean(dueFrom) ||
      Boolean(dueTo) ||
      (dateRange && dateRange !== DATE_RANGES.SELECT_TIME)
    );
  }, [selectedAssignee, selectedTaskType, selectedLabels, selectedPriorities, dueFrom, dueTo, dateRange]);

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (selectedAssignee && !selectedAssignee.includes('all')) count++;
    if (selectedTaskType && !selectedTaskType.includes('all')) count++;
    if (selectedLabels && !selectedLabels.includes('all')) count++;
    if (selectedPriorities && !selectedPriorities.includes('all')) count++;
    if (dueFrom) count++;
    if (dueTo) count++;
    if (dateRange && dateRange !== DATE_RANGES.SELECT_TIME) count++;
    return count;
  }, [selectedAssignee, selectedTaskType, selectedLabels, selectedPriorities, dueFrom, dueTo, dateRange]);

  return (
    <div className="dashboard">
      <Navbar />

      <main className="main reports-dashboard">
        {/* Header matching Task Management dashboard style */}
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          sx={{ 
            px: 2, 
            py: 1, 
            borderRadius: '8px' 
          }}
        >
          <Typography variant="h6" fontWeight={600} color={COLORS.text.muted}>
            {tr('reports.title')}
          </Typography>
        </Box>

        <FilterHeader
          viewMode="reports"
          active={isAnyFilterActive}
          appliedCount={appliedFiltersCount}
          selectedProject={selectedProject}
          onProjectChange={(e) => resetReportFiltersForProject(e.target.value)}
          projectDropdownsByUser={projectDropdownsByUser}
          dropdowns={{ assignees: dropdowns.assignees, types: dropdowns.types, labels: dropdowns.labels, priorities: dropdowns.priorities }}
          onClearFilters={handleClearFilters}
          getPriorityColor={getPriorityColor}
          getStatusColor={() => COLORS.gray[800]}
          reportFilterDropdowns={reportFilterDropdowns}
          reportVisibleFilters={visibleFilters}
          reportAvailableFilters={availableFilters}
          onReportFilterClick={handleAddFilterSelect}
          onReportRemoveFilter={handleRemoveFilter}
          reportActions={(
            <Tooltip
              title={tr('reports.exportToPDF') || 'Export to PDF'}
              placement="bottom"
              disableInteractive
              open={activeActionTooltip === 'export'}
              onOpen={() => setActiveActionTooltip('export')}
              onClose={() => setActiveActionTooltip(null)}
              slotProps={reportIconTooltipSlotProps}
            >
              <span>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveActionTooltip(null);
                    handleExportPDF();
                  }}
                  disabled={!areChartsReady}
                  className={`report__reset-button ${!areChartsReady ? 'report__reset-button--disabled' : ' '}`}
                  sx={{ borderRadius: '4px' }}
                >
                  <img src={ICONS.download} alt="" style={{ width: 18, height: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        />


        {/* 1st Row - Calendar Chart and Due Date Table (Fixed at top) */}
        <Box sx={{ ...reportStyles.flexGap3Mb3, alignItems: 'stretch' }}>
          <Box sx={{ width: '50%', display: 'flex' }}>
            <CalendarChart
              selectedMonth={selectedMonth}
              setSelectedMonth={setSelectedMonth}
              selectedYear={selectedYear}
              setSelectedYear={setSelectedYear}
              calendarData={calendarData}
              calendarData2={calendarData2}
              isMultiMonthRange={isMultiMonthRange}
              isTwoMonthRange={isTwoMonthRange}
              startDate={startDate}
              months={DATE_PICKER_MONTHS}
              getTicketColor={getTicketColor}
              handleDateClick={handleDateClick}
              defaultExpanded={true}
              canCollapse={false}
              isLoading={ticketsCountLoading}
            />
          </Box>
          <Box sx={{ width: '50%', display: 'flex' }}>
            <DueDateTable
              dueDateFilter={dueDateFilter}
              setDueDateFilter={setDueDateFilter}
              dueDateData={dueDateData}
              defaultExpanded={true}
              canCollapse={false}
              isLoading={overdueLoading}
            />
          </Box>
        </Box>

        {/* Dynamic Rows based on Pinning */}
        {(() => {
          const availableCharts = [
            { id: 'taskType', Component: TaskTypeChart, props: { data: taskTypeTicketData, chartView: taskTypeChartView, setChartView: handleSetTaskTypeChartView, onZoomClick: () => setTaskTypeChartModalOpen(true), dataLimit: taskTypeLimit, setDataLimit: setTaskTypeLimit, isLoading: loading } },
            { id: 'assignee', Component: AssigneeChart, props: { data: assigneeTicketData, chartView: chartView, setChartView: handleSetChartView, onZoomClick: () => setAssigneeChartModalOpen(true), dataLimit: assigneeLimit, setDataLimit: setAssigneeLimit, isLoading: loading } },
            { id: 'labels', Component: LabelsChart, props: { data: labelsTicketData, chartView: labelsChartView, setChartView: handleSetLabelsChartView, onZoomClick: () => setLabelsChartModalOpen(true), dataLimit: labelsLimit, setDataLimit: setLabelsLimit, isLoading: loading } },
            { id: 'priority', Component: PriorityChart, props: { data: priorityTicketDataAPI, chartView: priorityChartView, setChartView: handleSetPriorityChartView, onZoomClick: () => setPriorityChartModalOpen(true), dataLimit: priorityLimit, setDataLimit: setPriorityLimit, isLoading: loading } },
            { id: 'status', Component: StatusChart, props: { data: statusTicketData, allData: statusTicketDataAll, chartView: statusChartView, setChartView: handleSetStatusChartView, onZoomClick: () => setStatusChartModalOpen(true), selectedStatuses: selectedStatuses, setSelectedStatuses: setSelectedStatuses, availableStatuses: availableStatuses, isLoading: loading } }
          ];

          const sortedCharts = availableCharts;

          const renderedRows = [];
          // Row 2 & 3 (50/50%)
          for (let i = 0; i < 4; i += 2) {
            const Chart1 = sortedCharts[i].Component;
            const Chart2 = sortedCharts[i+1].Component;
            renderedRows.push(
              <Box key={`row-${i}`} sx={reportStyles.flexGap3Mb3}>
                <Box sx={{ width: '50%' }}>
                  <Chart1
                    {...sortedCharts[i].props} 
                    isPinned={pinnedCharts.includes(sortedCharts[i].id)}
                    onPin={() => handleTogglePin(sortedCharts[i].id)}
                    defaultExpanded={false}
                  />
                </Box>
                <Box sx={{ width: '50%' }}>
                  <Chart2
                    {...sortedCharts[i+1].props} 
                    isPinned={pinnedCharts.includes(sortedCharts[i+1].id)}
                    onPin={() => handleTogglePin(sortedCharts[i+1].id)}
                    defaultExpanded={false}
                  />
                </Box>
              </Box>
            );
          }
          // Row 4 (100%)
          const LastChart = sortedCharts[4].Component;
          renderedRows.push(
            <Box key="row-last" sx={reportStyles.flexGap3Mb3}>
              <Box sx={{ width: '100%' }}>
                <LastChart
                  {...sortedCharts[4].props} 
                  isPinned={pinnedCharts.includes(sortedCharts[4].id)}
                  onPin={() => handleTogglePin(sortedCharts[4].id)}
                  defaultExpanded={false}
                />
              </Box>
            </Box>
          );
          return renderedRows;
        })()}



        {/* Assignee Chart Enlarged Modal */}
        <Modal open={assigneeChartModalOpen} onClose={() => setAssigneeChartModalOpen(false)}>
          <Box sx={reportStyles.modalBox}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{tr('reports.charts.assigneeVsTicketCount')}</Typography>
              <Box display="flex" gap={1} alignItems="center">
                <DataLimitSelector value={assigneeLimit} onChange={(e) => setAssigneeLimit(e.target.value)} />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Box component="img" src={ICONS.pieChart} sx={{ width: 18, height: 18 }} />}
                  onClick={() => setChartView('pie')}
                  sx={reportStyles.buttonOutlined}
                >
                  Pie
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Box component="img" src={ICONS.barGraph} sx={{ width: 18, height: 18 }} />}
                  onClick={() => setChartView('bar')}
                  sx={reportStyles.buttonOutlined}
                >
                  Bar
                </Button>
                <IconButton onClick={() => setAssigneeChartModalOpen(false)} size="small">
                  <Close />
                </IconButton>
              </Box>
            </Box>
            {chartView === 'pie' && <PieChartView data={assigneeTicketData} height="calc(80vh - 100px)" outerRadius={200} />}
            {chartView === 'bar' && <BarChartView data={assigneeTicketData} height="calc(80vh - 100px)" />}
          </Box>
        </Modal>

        {/* Status Chart Enlarged Modal */}
        <Modal open={statusChartModalOpen} onClose={() => setStatusChartModalOpen(false)}>
          <Box sx={reportStyles.modalBox}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{tr('reports.charts.statusVsTicketCount')}</Typography>
              <Box display="flex" gap={1} alignItems="center">
                <Button variant="outlined" size="small" startIcon={<Box component="img" src={ICONS.pieChart} sx={{ width: 18, height: 18 }} />} onClick={() => setStatusChartView('pie')} sx={reportStyles.buttonOutlined}>Pie</Button>
                <Button variant="outlined" size="small" startIcon={<Box component="img" src={ICONS.barGraph} sx={{ width: 18, height: 18 }} />} onClick={() => setStatusChartView('bar')} sx={reportStyles.buttonOutlined}>Bar</Button>
                <IconButton onClick={() => setStatusChartModalOpen(false)} size="small"><Close /></IconButton>
              </Box>
            </Box>
            {statusChartView === 'pie' && <PieChartView data={statusTicketData} height="calc(80vh - 100px)" outerRadius={200} />}
            {statusChartView === 'bar' && <BarChartView data={statusTicketData} height="calc(80vh - 100px)" />}
          </Box>
        </Modal>

        {/* Task Type Chart Enlarged Modal */}
        <Modal open={taskTypeChartModalOpen} onClose={() => setTaskTypeChartModalOpen(false)}>
          <Box sx={reportStyles.modalBox}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{tr('reports.charts.taskTypeVsTicketCount')}</Typography>
              <Box display="flex" gap={1} alignItems="center">
                <DataLimitSelector value={taskTypeLimit} onChange={(e) => setTaskTypeLimit(e.target.value)} />
                <Button variant="outlined" size="small" startIcon={<Box component="img" src={ICONS.pieChart} sx={{ width: 18, height: 18 }} />} onClick={() => setTaskTypeChartView('pie')} sx={reportStyles.buttonOutlined}>Pie</Button>
                <Button variant="outlined" size="small" startIcon={<Box component="img" src={ICONS.barGraph} sx={{ width: 18, height: 18 }} />} onClick={() => setTaskTypeChartView('bar')} sx={reportStyles.buttonOutlined}>Bar</Button>
                <IconButton onClick={() => setTaskTypeChartModalOpen(false)} size="small"><Close /></IconButton>
              </Box>
            </Box>
            {taskTypeChartView === 'pie' && <PieChartView data={taskTypeTicketData} height="calc(80vh - 100px)" outerRadius={200} />}
            {taskTypeChartView === 'bar' && <BarChartView data={taskTypeTicketData} height="calc(80vh - 100px)" />}
          </Box>
        </Modal>

        {/* Labels Chart Enlarged Modal */}
        <Modal open={labelsChartModalOpen} onClose={() => setLabelsChartModalOpen(false)}>
          <Box sx={reportStyles.modalBox}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{tr('reports.charts.labelsVsTicketCount')}</Typography>
              <Box display="flex" gap={1} alignItems="center">
                <DataLimitSelector value={labelsLimit} onChange={(e) => setLabelsLimit(e.target.value)} />
                <Button variant="outlined" size="small" startIcon={<Box component="img" src={ICONS.pieChart} sx={{ width: 18, height: 18 }} />} onClick={() => setLabelsChartView('pie')} sx={reportStyles.buttonOutlined}>Pie</Button>
                <Button variant="outlined" size="small" startIcon={<Box component="img" src={ICONS.barGraph} sx={{ width: 18, height: 18 }} />} onClick={() => setLabelsChartView('bar')} sx={reportStyles.buttonOutlined}>Bar</Button>
                <IconButton onClick={() => setLabelsChartModalOpen(false)} size="small"><Close /></IconButton>
              </Box>
            </Box>
            {labelsChartView === 'pie' && <PieChartView data={labelsTicketData} height="calc(80vh - 100px)" outerRadius={200} />}
            {labelsChartView === 'bar' && <BarChartView data={labelsTicketData} height="calc(80vh - 100px)" />}
          </Box>
        </Modal>

        {/* Priority Chart Enlarged Modal */}
        <Modal open={priorityChartModalOpen} onClose={() => setPriorityChartModalOpen(false)}>
          <Box sx={reportStyles.modalBox}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{tr('reports.charts.priorityVsTicketCount')}</Typography>
              <Box display="flex" gap={1} alignItems="center">
                <DataLimitSelector value={priorityLimit} onChange={(e) => setPriorityLimit(e.target.value)} />
                <Button variant="outlined" size="small" startIcon={<Box component="img" src={ICONS.pieChart} sx={{ width: 18, height: 18 }} />} onClick={() => setPriorityChartView('pie')} sx={reportStyles.buttonOutlined}>Pie</Button>
                <Button variant="outlined" size="small" startIcon={<Box component="img" src={ICONS.barGraph} sx={{ width: 18, height: 18 }} />} onClick={() => setPriorityChartView('bar')} sx={reportStyles.buttonOutlined}>Bar</Button>
                <IconButton onClick={() => setPriorityChartModalOpen(false)} size="small"><Close /></IconButton>
              </Box>
            </Box>
            {priorityChartView === 'pie' && <PieChartView data={priorityTicketDataAPI} height="calc(80vh - 100px)" outerRadius={200} />}
            {priorityChartView === 'bar' && <BarChartView data={priorityTicketDataAPI} height="calc(80vh - 100px)" />}
          </Box>
        </Modal>

        {/* Tickets Modal */}
        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <Box sx={reportStyles.ticketsModalBox}>
            <Box sx={reportStyles.ticketsModalHeader}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{tr('reports.ticketsModal.ticketsCreatedOn')} {selectedDate}</Typography>
              <IconButton onClick={() => setModalOpen(false)} size="small">
                <Close />
              </IconButton>
            </Box>
            <Box sx={reportStyles.ticketsModalContent}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: showTicketsDueDateColumn ? '40%' : '50%', fontSize: '15px' }}>{tr('reports.upcomingDueDates.tableHeaders.title')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: showTicketsDueDateColumn ? '20%' : '25%', fontSize: '15px' }}>{tr('reports.upcomingDueDates.tableHeaders.assignee')}</TableCell>
                      {showTicketsDueDateColumn ? (
                        <TableCell sx={{ fontWeight: 600, width: '15%', fontSize: '15px' }}>{tr('reports.upcomingDueDates.tableHeaders.dueDate')}</TableCell>
                      ) : null}
                      <TableCell sx={{ fontWeight: 600, width: showTicketsDueDateColumn ? '12.5%' : '17%', fontSize: '15px' }}>{tr('reports.ticketsModal.status')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: showTicketsDueDateColumn ? '12.5%' : '18%', fontSize: '15px' }}>{tr('reports.ticketsModal.priority')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!ticketsList ? (
                      <TableRow>
                        <TableCell colSpan={showTicketsDueDateColumn ? 5 : 4} align="center" sx={{ py: 8 }}>
                          <Box display="flex" flexDirection="column" gap={1} alignItems="center">
                            {[...Array(5)].map((_, i) => (
                              <Skeleton key={i} variant="rounded" width="100%" height={40} />
                            ))}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : ticketsList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={showTicketsDueDateColumn ? 5 : 4} align="center" sx={{ py: 8 }}>
                          <Typography variant="body2" color="text.secondary">{t("reports.noDataAvailable")}</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      ticketsList.map((ticket) => (
                        <TicketRow key={ticket.ticket_number} ticket={ticket} showDueDate={showTicketsDueDateColumn} />
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </Modal>

        {/* Custom Date Range Modal */}
        <Dialog open={isCustomRangeModalOpen} onClose={() => setIsCustomRangeModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>{tr('reports.customDateRange.title')}</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={tr('reports.customDateRange.fromDate')}
                    value={tempDateFrom}
                    onChange={(e) => setTempDateFrom(e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label={tr('reports.customDateRange.toDate')}
                    value={tempDateTo}
                    onChange={(e) => setTempDateTo(e.target.value)}
                    onKeyDown={(e) => e.preventDefault()}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setIsCustomRangeModalOpen(false);
              setTempDateFrom('');
              setTempDateTo('');
            }}>{tr('reports.customDateRange.cancel')}</Button>
            <Button onClick={handleCustomRangeSubmit} variant="contained">{tr('reports.customDateRange.apply')}</Button>
          </DialogActions>
        </Dialog>
      </main>
    </div>
  );
};

export default Dashboard;
